import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Menu, MenuButton, MenuItem, MenuItems, MenuSeparator } from '@headlessui/react';
import { Battery, Signal, Thermometer, WifiOff, Droplets, GripHorizontal, Clock, Bell, AlarmClock, Wrench, Power, Pencil, Trash2, SlidersHorizontal, Upload, MoreHorizontal, MapPin, AlertTriangle } from 'lucide-react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { Sensor } from '../../types';
import { SensorRow, isSplitRow } from '../../utils/expandSensors';
// import { useGetLiveDataQuery } from '../../services/sensorDataApi'; // Removed - now passed as prop
import { getBatteryStatus } from '../../utils/batteryUtils';
import { getSignalStatus, getRssiStatus } from '../../utils/signalUtils';
import { formatSensorSerial, isNewerVersion } from '../../utils/sensorUtils';
import { normalizeAlarmType } from '../../utils/alarmDisplay';
import { formatDateTime } from '../../utils/dateUtils';
import { usePermissions } from '../../hooks/usePermissions';
import { LiveDataResponse } from '../../types/sensorData';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../../store/store';
import { useAcknowledgeAlarmLogMutation } from '../../services/alarmApi';
import { alarmApi } from '../../services/alarmApi';
import { Dialog } from '../common/Dialog';
import { AlarmAcknowledgeDialog } from '../alarms/AlarmAcknowledgeDialog';
import { logAuditEvent, AUDIT_ACTIONS } from '../../utils/auditLogger';
import { BackfillProgressBar } from '../common/BackfillProgressBar';
import { BackfillStatus } from '../../services/backfillApi';
import { API_CONFIG } from '../../config/api.config';
import { prepareCommonHeaders } from '../../services/baseApi';

interface SensorTableProps {
  sensors: SensorRow[];
  alarmLog: any[];
  alarmSettingsMap?: { [sensorId: number]: any };
  backfillStatus?: BackfillStatus[];
  liveData?: any[]; // Add liveData as prop
  onUpdateFirmware?: (sensorId: number) => void;
  showFirmwareUpdate?: boolean;
  latestFirmwareVersion?: string | null;
  onEdit?: (sensor: Sensor) => void;
  onDelete?: (sensor: Sensor) => void;
  onToggleServiceStatus?: (sensor: Sensor) => void;
  enableDragAndDrop?: boolean;
  showActions?: boolean; // Add showActions prop
  /** When false, hides alarm row styling, bells, countdown, duration, and Cancel Alarm (e.g. Sensors admin page). */
  showAlarmUi?: boolean;
  /** Distinct bordered rows (e.g. Sensors admin page). */
  rowBorders?: boolean;
  /** When false, hides the live temperature reading (e.g. setup pages where live data is not fetched). */
  showLiveTemp?: boolean;
  /** Row actions shown as a compact menu (sensors admin). Other pages keep inline buttons unless enabled. */
  preferActionMenu?: boolean;
  /** Softer bordered rows instead of bold indigo (pairs with Sensors admin styling). */
  adminListSurface?: boolean;
  /** Hides temperature / alarms “Data” column (e.g. Sensors admin focuses on roster + telemetry status). */
  hideDataColumn?: boolean;
  /** Set of sensor IDs currently in signal test mode — shows prominent RSSI/SNR and exact timestamps. */
  signalTestSensorIds?: Set<number>;
}

interface SortableSensorRowProps {
  sensor: SensorRow;
  liveData?: LiveDataResponse;
  onRowClick: (sensorId: number, channel?: 1 | 2) => void;
  provided: any;
  isDragging: boolean;
  latestAlarm?: any;
  preAlarm?: any;
  preAlarmTimeLeft?: number;
  backfillStatus?: BackfillStatus[];
  onUpdateFirmware?: (sensorId: number) => void;
  showFirmwareUpdate?: boolean;
  latestFirmwareVersion?: string | null;
  onEdit?: (sensor: Sensor) => void;
  onDelete?: (sensor: Sensor) => void;
  onToggleServiceStatus?: (sensor: Sensor) => void;
  enableDragAndDrop?: boolean;
  onCalibrate?: (sensor: Sensor) => void;
  showActions?: boolean;
  /** Opens alarm detail modal (dashboard): pass active alarm row. */
  onAlarmBellClick?: (sensor: Sensor, alarmRow: any) => void;
  now?: number;
  showAlarmUi?: boolean;
  rowBorders?: boolean;
  showLiveTemp?: boolean;
  preferActionMenu?: boolean;
  adminListSurface?: boolean;
  hideDataColumn?: boolean;
  /** True when this specific row's sensor is in signal test mode. */
  isSignalTest?: boolean;
}

// Add CSS for bell animation and drag animations
const bellStyles = `
@keyframes bell-flash-red {
  0%, 100% { color: #ef4444; opacity: 1; }
  50% { color: #b91c1c; opacity: 0.3; }
}
@keyframes bell-flash-blue {
  0%, 100% { color: #3b82f6; opacity: 1; }
  50% { color: #1e40af; opacity: 0.3; }
}
@keyframes bell-flash-orange {
  0%, 100% { color: #f59e42; opacity: 1; }
  50% { color: #b45309; opacity: 0.3; }
}
.bell-flash-red {
  animation: bell-flash-red 1s infinite;
}
.bell-flash-blue {
  animation: bell-flash-blue 1s infinite;
}
.bell-flash-orange {
  animation: bell-flash-orange 1s infinite;
}

@keyframes alarm-row-high {
  0%, 100% { background-color: rgba(239, 68, 68, 0.28); }
  50% { background-color: rgba(239, 68, 68, 0.58); }
}
@keyframes alarm-row-low {
  0%, 100% { background-color: rgba(59, 130, 246, 0.28); }
  50% { background-color: rgba(59, 130, 246, 0.58); }
}
@keyframes alarm-row-pre {
  0%, 100% { background-color: rgba(249, 115, 22, 0.20); }
  50% { background-color: rgba(249, 115, 22, 0.48); }
}
.alarm-row-high {
  animation: alarm-row-high 1.2s ease-in-out infinite;
  box-shadow: inset 4px 0 0 #ef4444;
}
.alarm-row-low {
  animation: alarm-row-low 1.2s ease-in-out infinite;
  box-shadow: inset 4px 0 0 #3b82f6;
}
.alarm-row-pre {
  animation: alarm-row-pre 1.2s ease-in-out infinite;
  box-shadow: inset 4px 0 0 #f97316;
}

@keyframes drag-pulse {
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.05); }
}

.drag-handle-hover {
  animation: drag-pulse 1s ease-in-out infinite;
}

@keyframes drop-zone-highlight {
  0%, 100% { background-color: rgba(59, 130, 246, 0.1); }
  50% { background-color: rgba(59, 130, 246, 0.2); }
}

.drop-zone-active {
  animation: drop-zone-highlight 1s ease-in-out infinite;
}
`;
if (typeof document !== 'undefined' && !document.getElementById('bell-flash-styles')) {
  const style = document.createElement('style');
  style.id = 'bell-flash-styles';
  style.innerHTML = bellStyles;
  document.head.appendChild(style);
}

// Helper to format mm:ss
function formatCountdown(ms: number) {
  if (ms <= 0) return '0:00';
  const totalSeconds = Math.floor(ms / 1000);
  const min = Math.floor(totalSeconds / 60);
  const sec = totalSeconds % 60;
  return `${min}:${sec.toString().padStart(2, '0')}`;
}

function formatElapsed(ms: number) {
  const totalSeconds = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  if (h > 0) return `${h}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

function SortableSensorRow({ sensor, liveData, onRowClick, provided, isDragging, latestAlarm, preAlarm, preAlarmTimeLeft, backfillStatus, onUpdateFirmware, showFirmwareUpdate, latestFirmwareVersion, onEdit, onDelete, onToggleServiceStatus, enableDragAndDrop, onCalibrate, showActions, onAlarmBellClick, now = Date.now(), showAlarmUi = true, rowBorders = false, showLiveTemp = true, preferActionMenu = false, adminListSurface = false, hideDataColumn = false, isSignalTest = false }: SortableSensorRowProps & { enableDragAndDrop?: boolean }) {
  const rowNavigate = useNavigate();
  const reading = liveData?.find(d => Number(d.sensor_id) === sensor.serialNo);
  const isOutOfService = !!sensor.is_out_of_service;
  
  // Find backfill status for this sensor
  const sensorBackfillStatus = backfillStatus?.find(status => status.sensor_id === sensor.serialNo.toString());

  let rowColor = '';
  let bell = null;
  if (isOutOfService) {
    rowColor = 'bg-gray-200/70 dark:bg-gray-800/70 grayscale opacity-50';
    bell = <Wrench className="h-4 w-4 text-gray-400 ml-2" />;
  } else if (showAlarmUi && preAlarm) {
    rowColor = 'alarm-row-pre';
    bell = <Bell className="h-4 w-4 bell-flash-orange text-orange-500 ml-2" />;
  } else if (showAlarmUi && latestAlarm) {
    const kind = normalizeAlarmType(String(latestAlarm.alarm_type));
    if (kind === 'low') {
      rowColor = 'alarm-row-low';
      if (!onAlarmBellClick) bell = <Bell className="h-4 w-4 bell-flash-blue text-blue-500 ml-2" />;
    } else if (kind === 'high') {
      rowColor = 'alarm-row-high';
      if (!onAlarmBellClick) bell = <Bell className="h-4 w-4 bell-flash-red text-red-500 ml-2" />;
    } else if (kind === 'battery') {
      rowColor = 'bg-rose-50/80 dark:bg-rose-950/30';
      if (!onAlarmBellClick) bell = <Bell className="h-4 w-4 bell-flash-orange text-rose-500 ml-2" />;
    } else if (kind === 'missing_data') {
      rowColor = 'bg-violet-50/80 dark:bg-violet-950/30';
      if (!onAlarmBellClick) bell = <Bell className="h-4 w-4 bell-flash-orange text-violet-500 ml-2" />;
    } else {
      rowColor = 'alarm-row-high';
      if (!onAlarmBellClick) bell = <Bell className="h-4 w-4 text-red-500 ml-2" />;
    }
  } else if (showAlarmUi) {
    bell = <Bell className="h-4 w-4 text-green-500 ml-2" />;
  }

  const isUpdateAvailable = () => {
    if (!showFirmwareUpdate || !latestFirmwareVersion || sensor.type !== '1') return false;
    return isNewerVersion(latestFirmwareVersion, sensor.firmware_version || '');
  };

  // Track if the row is being dragged to prevent click navigation
  const dragRef = useRef(false);

  return (
    <>
      <tr
        ref={provided.innerRef}
        {...provided.draggableProps}
        className={`group transition-all duration-200 ${
          rowBorders
            ? [
                adminListSurface
                  ? [
                      '[&>td]:border-solid [&>td]:border-gray-200/90 dark:[&>td]:border-gray-600/80',
                      '[&>td]:border [&>td]:bg-inherit',
                      '[&>td:first-child]:border-l [&>td:first-child]:rounded-l-xl',
                      '[&>td:last-child]:border-r [&>td:last-child]:rounded-r-xl',
                      !rowColor ? 'bg-white/90 dark:bg-gray-900/50' : '',
                      'shadow-sm dark:shadow-none',
                    ]
                  : [
                      '[&>td]:border-solid [&>td]:border-indigo-500 dark:[&>td]:border-indigo-400',
                      '[&>td]:border-t-2 [&>td]:border-b-2 [&>td]:bg-inherit',
                      '[&>td:first-child]:border-l-2 [&>td:first-child]:rounded-l-lg',
                      '[&>td:last-child]:border-r-2 [&>td:last-child]:rounded-r-lg',
                      !rowColor ? 'bg-gray-50 dark:bg-gray-800' : '',
                      'shadow-sm',
                    ],
              ].flat().filter(Boolean).join(' ')
            : ''
        } ${
          isDragging
            ? 'shadow-2xl bg-blue-50 dark:bg-blue-900/20 scale-[1.02] rotate-1 z-10'
            : enableDragAndDrop
              ? `${!isOutOfService ? 'cursor-pointer' : ''} hover:bg-indigo-50/80 dark:hover:bg-indigo-950/35 hover:outline hover:outline-2 hover:outline-indigo-400/50 hover:z-[1]`
              : rowBorders
                ? `${!isOutOfService ? 'cursor-pointer' : ''} hover:[&>td]:bg-gray-100/90 dark:hover:[&>td]:bg-gray-700/80`
                : `${!isOutOfService ? 'cursor-pointer' : ''} hover:bg-gray-50 dark:hover:bg-gray-800 hover:outline hover:outline-2 hover:outline-indigo-400/50 hover:z-[1]`
        } ${rowColor}`}
        style={{
          ...provided.draggableProps.style,
          transform: isDragging ? provided.draggableProps.style?.transform : undefined,
        }}
        onMouseDown={enableDragAndDrop ? () => { dragRef.current = false; } : undefined}
        onDragStart={enableDragAndDrop ? () => { dragRef.current = true; } : undefined}
        onDragEnd={enableDragAndDrop ? () => { setTimeout(() => { dragRef.current = false; }, 0); } : undefined}
        onClick={() => {
          if (!isOutOfService && !dragRef.current) {
            onRowClick(sensor.id, isSplitRow(sensor) ? sensor._channel : undefined);
          }
        }}
      >
        <td className={`px-1 sm:px-4 py-1 sm:py-2 min-w-0 align-top ${hideDataColumn ? 'w-[40%] sm:w-auto' : 'w-[32%] sm:w-auto'}`}>
          <div className="flex items-center min-w-0">
            {/* Drag handle: only this is draggable */}
            {enableDragAndDrop && (
              <div
                {...provided.dragHandleProps}
                className={`p-0.5 sm:p-1 mr-1 sm:mr-2 rounded transition-all duration-200 flex-shrink-0 ${
                  isDragging 
                    ? 'cursor-grabbing bg-blue-100 dark:bg-blue-900/30 scale-110' 
                    : 'cursor-grab hover:bg-gray-100 dark:hover:bg-gray-700 hover:scale-105'
                }`}
                title="Drag to reorder"
                aria-label="Drag to reorder"
              >
                <GripHorizontal className={`h-4 w-4 transition-colors duration-200 ${
                  isDragging 
                    ? 'text-blue-600 dark:text-blue-400' 
                    : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-300'
                }`} />
              </div>
            )}
            <div className="min-w-0 flex-1">
              <div className="text-xs sm:text-sm font-medium truncate flex items-center gap-1.5 min-w-0 text-gray-900 dark:text-white">
                <span className={`truncate min-w-0 flex-1 ${isOutOfService ? 'line-through' : ''}`}>
                  {sensor.name}
                </span>
                <div className="flex flex-row items-stretch gap-1.5 sm:gap-2 flex-shrink-0">
                  {showAlarmUi && latestAlarm && !preAlarm && onAlarmBellClick && (
                    <div className="flex w-12 sm:w-14 flex-col items-stretch gap-px">
                      <div className="flex min-h-[0.875rem] items-end justify-center px-0.5">
                        <span className="text-[8px] sm:text-[9px] font-medium text-gray-500 dark:text-gray-400 leading-none text-center">
                          <span className="sm:hidden">Alarm</span>
                          <span className="hidden sm:inline">Alarm State</span>
                        </span>
                      </div>
                      <div className="flex justify-center">
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); onAlarmBellClick(sensor, latestAlarm); }}
                          className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-md transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 dark:focus-visible:ring-offset-gray-900 ${
                            (() => {
                              const ak = normalizeAlarmType(String(latestAlarm.alarm_type));
                              if (ak === 'low') return 'text-blue-600 dark:text-blue-400 hover:bg-blue-500/15 dark:hover:bg-blue-400/10 focus-visible:ring-blue-500/60';
                              if (ak === 'battery') return 'text-rose-600 dark:text-rose-400 hover:bg-rose-500/15 dark:hover:bg-rose-400/10 focus-visible:ring-rose-500/60';
                              if (ak === 'missing_data') return 'text-violet-600 dark:text-violet-400 hover:bg-violet-500/15 dark:hover:bg-violet-400/10 focus-visible:ring-violet-500/60';
                              return 'text-red-600 dark:text-red-400 hover:bg-red-500/15 dark:hover:bg-red-400/10 focus-visible:ring-red-500/60';
                            })()
                          }`}
                          title="Alarm details & acknowledge"
                          aria-label="Open alarm details and acknowledge"
                        >
                          <Bell className="h-3.5 w-3.5 sm:h-4 sm:w-4 opacity-90" aria-hidden />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
                {isOutOfService && (
                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-gray-200 dark:bg-gray-700 text-[9px] sm:text-[10px] font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide flex-shrink-0">
                    <Wrench className="h-2.5 w-2.5" />
                    <span className="hidden sm:inline">Out of service</span>
                    <span className="sm:hidden">OOS</span>
                  </span>
                )}
              </div>
              <div className="text-[10px] sm:text-sm text-gray-500 dark:text-gray-400 truncate flex items-center gap-1">
                <span className="truncate">{formatSensorSerial(sensor.serialNo, sensor.type)}</span>
                {sensor.type === '1' && (
                  <span
                    className={`inline-flex items-center px-1 py-0.5 rounded text-[8px] sm:text-[9px] font-semibold uppercase tracking-wide flex-shrink-0 ${
                      (sensor.probe_type ?? 'internal') === 'external'
                        ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'
                        : 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                    }`}
                  >
                    {(sensor.probe_type ?? 'internal') === 'external' ? 'External' : 'Internal'}
                  </span>
                )}
              </div>
            </div>
          </div>
        </td>
        {!hideDataColumn && (
          <td className="px-1 sm:px-4 py-1 sm:py-2 w-[28%] sm:w-auto min-w-0 align-top">
          <div className="flex flex-col min-w-0 gap-0">
            <div className="flex items-center min-w-0">
              {showLiveTemp && (
                <>
                  <Thermometer className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-gray-400 flex-shrink-0" />
                  <span className="ml-1 sm:ml-2 text-xs sm:text-sm text-gray-900 dark:text-white truncate min-w-0 flex-1">
                    {isSplitRow(sensor)
                      ? (() => {
                          const val = sensor._channel === 1
                            ? reading?.temperature
                            : reading?.temperature_2;
                          return (typeof val === 'number' && !isNaN(val))
                            ? val.toFixed(1) + '°C'
                            : '----';
                        })()
                      : (sensor.type === '5' || sensor.type === '6')
                        ? `${
                            (typeof reading?.temperature === 'number' && !isNaN(reading.temperature))
                              ? reading.temperature.toFixed(1) + '°C'
                              : '----'
                          } / ${
                            (typeof reading?.temperature_2 === 'number' && !isNaN(reading.temperature_2))
                              ? reading.temperature_2.toFixed(1) + '°C'
                              : '----'
                          }`
                        : (
                          (typeof reading?.temperature === 'number' && !isNaN(reading.temperature))
                            ? `${reading.temperature.toFixed(sensor.type === '7' ? 2 : 1)}°C`
                            : '----'
                        )
                    }
                  </span>
                </>
              )}
              {(() => {
                // Shown when the most recent thing this sensor sent was a
                // reading MQTTConsumer rejected as invalid (NaN/out of
                // range) rather than a good one — see
                // sensorService.js flagInvalidReading(). Otherwise a bad
                // reading is silently dropped and the row just looks stale,
                // with no indication of why.
                if (!sensor.last_invalid_reading_at) return null;
                const invalidAt = new Date(sensor.last_invalid_reading_at).getTime();
                const lastGoodAt = reading?.timestamp ? new Date(reading.timestamp).getTime() : 0;
                if (Number.isNaN(invalidAt) || invalidAt <= lastGoodAt) return null;
                return (
                  <span
                    className="ml-1 inline-flex items-center flex-shrink-0 text-amber-600 dark:text-amber-400"
                    title={`Sensor sent an invalid reading at ${new Date(sensor.last_invalid_reading_at).toLocaleString('en-GB')} — dropped, not shown in the graph.`}
                  >
                    <AlertTriangle className="h-3.5 w-3.5 sm:h-4 sm:w-4" aria-label="Invalid reading received" />
                  </span>
                );
              })()}
              {bell}
            </div>
            {showAlarmUi && preAlarm && (
              <div
                className="mt-0.5 text-sm sm:text-base font-mono tabular-nums font-semibold flex items-center gap-0.5 leading-none text-orange-600 dark:text-orange-400"
                title={`Pre-alarm ${formatCountdown(preAlarmTimeLeft ?? 0)}`}
                aria-label={`Pre-alarm countdown ${formatCountdown(preAlarmTimeLeft ?? 0)}`}
              >
                <AlarmClock className="h-3 w-3 sm:h-3.5 sm:w-3.5 flex-shrink-0 opacity-90 text-orange-500" aria-hidden />
                <span>{formatCountdown(preAlarmTimeLeft ?? 0)}</span>
              </div>
            )}
            {showAlarmUi && latestAlarm && !preAlarm && (
              <div className="mt-0.5 flex flex-wrap items-center gap-1.5 min-w-0">
                {latestAlarm.activated && (
                  <div
                    className={`text-sm sm:text-base font-mono tabular-nums font-semibold flex items-center gap-0.5 leading-none ${
                      (() => {
                        const ak = normalizeAlarmType(String(latestAlarm.alarm_type));
                        if (ak === 'low') return 'text-blue-600 dark:text-blue-400';
                        if (ak === 'battery') return 'text-rose-600 dark:text-rose-400';
                        if (ak === 'missing_data') return 'text-violet-600 dark:text-violet-400';
                        return 'text-red-600 dark:text-red-400';
                      })()
                    }`}
                    aria-label={`Alarm duration ${formatElapsed(now - new Date(latestAlarm.activated).getTime())}`}
                  >
                    <Clock className="h-3 w-3 sm:h-3.5 sm:w-3.5 flex-shrink-0 opacity-90" aria-hidden />
                    <span>{formatElapsed(now - new Date(latestAlarm.activated).getTime())}</span>
                  </div>
                )}
              </div>
            )}
            {showLiveTemp && sensor.type === '3' && reading?.humidity !== undefined && (
              <div className="flex items-center mt-0.5">
                <Droplets className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-gray-400 flex-shrink-0" />
                <span className="ml-1 sm:ml-2 text-xs sm:text-sm text-gray-900 dark:text-white truncate">
                  {reading.humidity.toFixed(1)}%
                </span>
              </div>
            )}
          </div>
        </td>
        )}
        <td className={`px-1 sm:px-4 py-1 sm:py-2 min-w-0 align-top ${hideDataColumn ? 'w-[32%] sm:w-auto' : 'w-[22%] sm:w-auto'}`}>
          {sensor.type === '4' || sensor.type === '5' ? (
            <span className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 font-semibold">Legacy</span>
          ) : (
            <div className="flex flex-col gap-0.5">
              <div className="flex items-center space-x-1 sm:space-x-4">
                <div className="flex items-center flex-shrink-0">
                  {(() => {
                    const snr = reading?.SNR || sensor.SNR;
                    const rssi = reading?.RSSI || sensor.RSSI;
                    const sig = snr ? getSignalStatus(snr) : getRssiStatus(rssi);
                    return <Signal className={`h-3 w-3 sm:h-4 sm:w-4 ${sig.iconColor}`} aria-label={sig.text} />;
                  })()}
                </div>
                <div className="flex items-center flex-shrink-0">
                  <Battery className="h-3 w-3 sm:h-4 sm:w-4 text-gray-400" />
                  {(() => {
                    const batteryValue = reading?.Battery || sensor.Battery;
                    return !isNaN(parseFloat(batteryValue)) && (
                      <span className={`ml-0.5 text-[10px] sm:text-sm ${getBatteryStatus(sensor.type, parseFloat(batteryValue)).color}`}>
                        {getBatteryStatus(sensor.type, parseFloat(batteryValue)).text}
                      </span>
                    );
                  })()}
                </div>
              </div>
              {/* RSSI / SNR — enlarged in signal test mode */}
              {(() => {
                const rssiVal = reading?.RSSI ?? sensor.RSSI;
                const snrVal = reading?.SNR ?? sensor.SNR;
                if (isSignalTest) {
                  return (
                    <div className="mt-1 flex flex-col gap-0.5">
                      {rssiVal != null && (
                        <div className="flex items-baseline gap-1">
                          <span className="text-[9px] font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500 w-8 shrink-0">RSSI</span>
                          <span className={`text-sm font-bold tabular-nums ${(() => { const snr = reading?.SNR ?? sensor.SNR; const rssi = reading?.RSSI ?? sensor.RSSI; return (snr ? getSignalStatus(snr) : getRssiStatus(rssi)).iconColor; })()}`}>
                            {rssiVal}{snrVal == null ? '%' : ' dBm'}
                          </span>
                        </div>
                      )}
                      {snrVal != null && (
                        <div className="flex items-baseline gap-1">
                          <span className="text-[9px] font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500 w-8 shrink-0">SNR</span>
                          <span className={`text-sm font-bold tabular-nums ${(() => { const snr = reading?.SNR ?? sensor.SNR; const rssi = reading?.RSSI ?? sensor.RSSI; return (snr ? getSignalStatus(snr) : getRssiStatus(rssi)).iconColor; })()}`}>
                            {snrVal}
                          </span>
                        </div>
                      )}
                      {rssiVal == null && snrVal == null && (
                        <span className="text-xs text-gray-400 dark:text-gray-500 italic">No signal data</span>
                      )}
                    </div>
                  );
                }
                return (
                  <>
                    <div className="sm:hidden flex flex-col gap-px text-[9px] leading-tight text-gray-400 dark:text-gray-500 tabular-nums">
                      <span>R:{rssiVal != null ? (snrVal != null ? `${rssiVal} dBm` : `${rssiVal}%`) : '—'}</span>
                      {snrVal != null && <span>S:{snrVal}</span>}
                    </div>
                    <div className="hidden sm:block text-[10px] text-gray-400 dark:text-gray-500 truncate">
                      {snrVal != null
                        ? `RSSI: ${rssiVal ?? '—'} dBm · SNR: ${snrVal}`
                        : `Signal: ${rssiVal != null ? `${rssiVal}%` : '—'}`}
                    </div>
                  </>
                );
              })()}
            </div>
          )}
        </td>
        <td className={`px-1 sm:px-4 py-1 sm:py-2 min-w-0 ${isSignalTest ? 'table-cell' : 'hidden md:table-cell'}`}>
          {(() => {
            // Check if we should show backfill progress bar
            if (sensorBackfillStatus && !isSignalTest) {
              const { remaining, updated_at } = sensorBackfillStatus;
              const progress_percent = Number(sensorBackfillStatus.progress_percent);
              const updateTime = new Date(updated_at).getTime();
              const nowMs = Date.now();
              const threeMinutesAgo = nowMs - (3 * 60 * 1000);
              const isActive = updateTime > threeMinutesAgo;
              const isComplete = progress_percent >= 100;
              const hasSignificantRemaining = remaining > 3;
              if (isActive && !isComplete && hasSignificantRemaining) {
                return <BackfillProgressBar backfillStatus={sensorBackfillStatus} />;
              }
            }

            const ts = reading?.timestamp;

            if (isSignalTest) {
              if (ts) {
                return (
                  <div className="flex flex-col gap-0.5">
                    <div className="flex items-center gap-1.5">
                      <span className="relative flex h-2 w-2 shrink-0">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                        <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
                      </span>
                      <time
                        dateTime={ts}
                        className="font-mono font-bold tabular-nums text-emerald-700 dark:text-emerald-300 text-sm"
                      >
                        {new Date(ts).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                      </time>
                    </div>
                    <span className="pl-3.5 text-[10px] text-gray-400 dark:text-gray-500 tabular-nums">
                      {formatDateTime(ts)}
                    </span>
                  </div>
                );
              }
              return (
                <div className="flex items-center gap-1.5">
                  <span className="relative flex h-2 w-2 shrink-0">
                    <span className="relative inline-flex h-2 w-2 rounded-full bg-amber-400" />
                  </span>
                  <span className="text-xs italic text-amber-700 dark:text-amber-400">Waiting…</span>
                </div>
              );
            }

            if (ts) {
              return (
                <div className="flex items-center text-xs text-gray-500 dark:text-gray-400 sm:text-sm">
                  <Clock className="mr-1 h-3 w-3 shrink-0 sm:mr-2 sm:h-4 sm:w-4" aria-hidden />
                  <time dateTime={ts}>{formatDateTime(ts, true)}</time>
                </div>
              );
            }
            const staleTitle = 'No timestamp from the live data feed for this sensor. It may be offline or outside the selected time window.';
            return (
              <div className="flex items-center text-xs sm:text-sm text-gray-500 dark:text-gray-400">
                <Clock className="mr-1 h-3 w-3 shrink-0 opacity-60 sm:mr-2 sm:h-4 sm:w-4" aria-hidden />
                <span
                  className={hideDataColumn ? 'italic text-amber-900/85 dark:text-amber-300/90' : ''}
                  title={staleTitle}
                >
                  {hideDataColumn ? 'No reading in feed' : '—'}
                </span>
              </div>
            );
          })()}
        </td>
        {showActions && (
          <td className={`px-1 sm:px-4 py-1 sm:py-2 min-w-0 align-top text-right ${preferActionMenu ? 'w-14 sm:w-[5.25rem]' : 'w-[18%] sm:w-auto'}`}>
            {preferActionMenu ? (
              <div className="flex flex-col items-end gap-1.5">
                {sensor.firmware_version && (
                  <span className="hidden sm:inline-block max-w-[5rem] truncate text-[10px] text-gray-400 dark:text-gray-500 tabular-nums" title={`Firmware ${sensor.firmware_version}`}>
                    v{sensor.firmware_version}
                  </span>
                )}
                <Menu>
                  <MenuButton
                    type="button"
                    className="inline-flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg border border-gray-200/90 bg-white text-gray-600 shadow-sm hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500/70 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700/80 dark:focus-visible:ring-indigo-400/60"
                    title="Sensor actions"
                    onClick={(e) => { e.stopPropagation(); }}
                  >
                    <span className="sr-only">Open sensor actions menu</span>
                    <MoreHorizontal className="h-5 w-5" aria-hidden />
                  </MenuButton>
                  <MenuItems
                    anchor="bottom end"
                    transition
                    className="z-[80] mt-1.5 w-56 rounded-xl border border-gray-200/90 bg-white p-1 shadow-lg outline-none [--anchor-gap:4px] data-[closed]:scale-95 data-[closed]:opacity-0 dark:border-gray-700 dark:bg-gray-900 dark:shadow-gray-950/60"
                  >
                    {sensor.zone_id && (
                      <MenuItem>
                        {({ focus }) => (
                          <button
                            type="button"
                            className={`${focus ? 'bg-gray-100 dark:bg-gray-800' : ''} group flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm text-gray-800 dark:text-gray-100`}
                            onClick={(e) => { e.stopPropagation(); rowNavigate(`/dashboard/plan?zone=${sensor.zone_id}&highlight=${sensor.id}`); }}
                          >
                            <MapPin className="h-4 w-4 shrink-0 text-emerald-500 group-hover:text-emerald-600 dark:text-emerald-400" aria-hidden />
                            View on Plan
                          </button>
                        )}
                      </MenuItem>
                    )}
                    {onEdit && (
                      <MenuItem>
                        {({ focus }) => (
                          <button
                            type="button"
                            className={`${focus ? 'bg-gray-100 dark:bg-gray-800' : ''} group flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm text-gray-800 dark:text-gray-100`}
                            onClick={(e) => { e.stopPropagation(); onEdit(sensor); }}
                          >
                            <Pencil className="h-4 w-4 shrink-0 text-gray-500 group-hover:text-indigo-600 dark:text-gray-400 dark:group-hover:text-indigo-400" aria-hidden />
                            Edit sensor
                          </button>
                        )}
                      </MenuItem>
                    )}
                    {onUpdateFirmware && isUpdateAvailable() && (
                      <MenuItem>
                        {({ focus }) => (
                          <button
                            type="button"
                            className={`${focus ? 'bg-gray-100 dark:bg-gray-800' : ''} group flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm text-gray-800 dark:text-gray-100`}
                            onClick={(e) => { e.stopPropagation(); onUpdateFirmware(sensor.id); }}
                          >
                            <Upload className="h-4 w-4 shrink-0 text-violet-500" aria-hidden />
                            Update firmware
                            <span className="ml-auto text-xs font-normal tabular-nums text-gray-400 dark:text-gray-500">{latestFirmwareVersion}</span>
                          </button>
                        )}
                      </MenuItem>
                    )}
                    {onCalibrate && (
                      <MenuItem>
                        {({ focus }) => (
                          <button
                            type="button"
                            className={`${focus ? 'bg-gray-100 dark:bg-gray-800' : ''} group flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm text-gray-800 dark:text-gray-100`}
                            onClick={(e) => { e.stopPropagation(); onCalibrate(sensor); }}
                          >
                            <SlidersHorizontal className="h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" aria-hidden />
                            Calibrate
                          </button>
                        )}
                      </MenuItem>
                    )}
                    {onToggleServiceStatus && (
                      <MenuItem>
                        {({ focus }) => (
                          <button
                            type="button"
                            className={`${focus ? 'bg-gray-100 dark:bg-gray-800' : ''} group flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm text-gray-800 dark:text-gray-100`}
                            onClick={(e) => { e.stopPropagation(); onToggleServiceStatus(sensor); }}
                          >
                            {isOutOfService ? (
                              <>
                                <Power className="h-4 w-4 shrink-0 text-emerald-600 dark:text-emerald-400" aria-hidden />
                                Return to service
                              </>
                            ) : (
                              <>
                                <Wrench className="h-4 w-4 shrink-0 text-gray-500" aria-hidden />
                                Mark out of service
                              </>
                            )}
                          </button>
                        )}
                      </MenuItem>
                    )}
                    {onDelete && (
                      <>
                        <MenuSeparator className="my-1 border-t border-gray-200 dark:border-gray-700" />
                        <MenuItem>
                          {({ focus }) => (
                            <button
                              type="button"
                              className={`${focus ? 'bg-red-50 dark:bg-red-950/35' : ''} group flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm font-medium text-red-700 dark:text-red-400`}
                              onClick={(e) => { e.stopPropagation(); onDelete(sensor); }}
                            >
                              <Trash2 className="h-4 w-4 shrink-0 opacity-90" aria-hidden />
                              Delete sensor
                            </button>
                          )}
                        </MenuItem>
                      </>
                    )}
                  </MenuItems>
                </Menu>
              </div>
            ) : (
              <div className="flex flex-row flex-wrap justify-end gap-1 sm:flex-col sm:items-stretch sm:gap-0 sm:space-y-1">
                {sensor.firmware_version && (
                  <div className="hidden sm:block text-[10px] sm:text-xs text-gray-400 dark:text-gray-500 truncate w-full text-right">
                    FW: {sensor.firmware_version}
                  </div>
                )}
                {onUpdateFirmware && isUpdateAvailable() && (
                  <button
                    type="button"
                    className="p-1.5 sm:px-2 sm:py-1 bg-purple-600 text-white rounded text-[10px] sm:text-xs font-medium hover:bg-purple-700 leading-tight inline-flex items-center justify-center gap-1"
                    onClick={e => { e.stopPropagation(); onUpdateFirmware(sensor.id); }}
                    title={`Update firmware to ${latestFirmwareVersion}`}
                  >
                    <Upload className="h-3.5 w-3.5 flex-shrink-0" />
                    <span className="hidden sm:inline whitespace-nowrap">Update FW</span>
                  </button>
                )}
                {onCalibrate && (
                  <button
                    type="button"
                    className="p-1.5 sm:px-2 sm:py-1 bg-yellow-600 text-white rounded text-[10px] sm:text-xs font-medium hover:bg-yellow-700 leading-tight inline-flex items-center justify-center gap-1"
                    onClick={e => { e.stopPropagation(); onCalibrate(sensor); }}
                    title="Calibrate sensor"
                  >
                    <SlidersHorizontal className="h-3.5 w-3.5 flex-shrink-0" />
                    <span className="hidden sm:inline whitespace-nowrap">Calibrate</span>
                  </button>
                )}
                {onToggleServiceStatus && (
                  <button
                    type="button"
                    className={`p-1.5 sm:px-2 sm:py-1 rounded text-[10px] sm:text-xs font-medium leading-tight inline-flex items-center justify-center gap-1 ${
                      isOutOfService
                        ? 'bg-emerald-600 text-white hover:bg-emerald-700'
                        : 'bg-gray-500 text-white hover:bg-gray-600'
                    }`}
                    onClick={e => { e.stopPropagation(); onToggleServiceStatus(sensor); }}
                    title={isOutOfService ? 'Mark as In Service' : 'Mark as Out of Service'}
                  >
                    {isOutOfService ? <Power className="h-3.5 w-3.5 flex-shrink-0" /> : <Wrench className="h-3.5 w-3.5 flex-shrink-0" />}
                    <span className="hidden sm:inline whitespace-nowrap">{isOutOfService ? 'Mark In Service' : 'Mark Out of Service'}</span>
                  </button>
                )}
                {onEdit && (
                  <button
                    type="button"
                    className="p-1.5 sm:px-2 sm:py-1 bg-blue-600 text-white rounded text-[10px] sm:text-xs font-medium hover:bg-blue-700 leading-tight inline-flex items-center justify-center gap-1"
                    onClick={e => { e.stopPropagation(); onEdit(sensor); }}
                    title="Edit sensor"
                  >
                    <Pencil className="h-3.5 w-3.5 flex-shrink-0" />
                    <span className="hidden sm:inline whitespace-nowrap">Edit</span>
                  </button>
                )}
                {onDelete && (
                  <button
                    type="button"
                    className="p-1.5 sm:px-2 sm:py-1 bg-red-600 text-white rounded text-[10px] sm:text-xs font-medium hover:bg-red-700 leading-tight inline-flex items-center justify-center gap-1"
                    onClick={e => { e.stopPropagation(); onDelete(sensor); }}
                    title="Delete sensor"
                  >
                    <Trash2 className="h-3.5 w-3.5 flex-shrink-0" />
                    <span className="hidden sm:inline whitespace-nowrap">Delete</span>
                  </button>
                )}
              </div>
            )}
          </td>
        )}
      </tr>
    </>
  );
}

export default function SensorTable({ sensors, alarmLog, alarmSettingsMap = {}, backfillStatus = [], liveData = [], onUpdateFirmware, showFirmwareUpdate = false, latestFirmwareVersion = null, onEdit, onDelete, onToggleServiceStatus, enableDragAndDrop = false, showActions = true, showAlarmUi = true, rowBorders = false, showLiveTemp = true, preferActionMenu = false, adminListSurface = false, hideDataColumn = false, signalTestSensorIds }: SensorTableProps) {
  const dispatch = useDispatch();
  const [isDragging, setIsDragging] = useState(false);
  const [orderedSensors, setOrderedSensors] = useState<Sensor[]>([]);
  /** Latest sensors from props — async order-loading must read this after awaits so deletes aren't overwritten by stale closures. */
  const sensorsRef = useRef<Sensor[]>(sensors);
  sensorsRef.current = sensors;
  /** Timestamp of the last completed drag. Used to discard in-flight DB fetches that started before the drag. */
  const lastDragAt = useRef(0);

  useEffect(() => {
    const ids = new Set(sensors.map((s) => Number(s.id)));
    setOrderedSensors((prev) => {
      const next = prev.filter((s) => ids.has(Number(s.id)));
      return next.length === prev.length ? prev : next;
    });
  }, [sensors]);

  const user = useSelector((state: RootState) => state.auth.user);
  const signalTestIds = signalTestSensorIds ?? new Set<number>();


  const [acknowledgeAlarmLog] = useAcknowledgeAlarmLogMutation();

  type AlarmModalState = { sensor: Sensor; anchorLog: any } | null;
  const [alarmModal, setAlarmModal] = useState<AlarmModalState>(null);
  const [alarmNoteDraft, setAlarmNoteDraft] = useState('');
  const [alarmAckSubmitting, setAlarmAckSubmitting] = useState(false);
  const [alarmAckError, setAlarmAckError] = useState<string | null>(null);

  const userDisplayName =
    user?.email || user?.firstName || (user?.id != null ? String(user.id) : '') || 'system';

  const openAlarmModal = (sensor: Sensor, anchorLog: any) => {
    if (!anchorLog) return;
    setAlarmNoteDraft(typeof anchorLog.notes === 'string' ? anchorLog.notes : '');
    setAlarmAckError(null);
    setAlarmModal({ sensor, anchorLog });
  };

  const closeAlarmModal = () => {
    if (alarmAckSubmitting) return;
    setAlarmModal(null);
    setAlarmNoteDraft('');
    setAlarmAckError(null);
  };

  // Pre-alarm state logic
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!showAlarmUi) return;
    const interval = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(interval);
  }, [showAlarmUi]);

  // Helper to get pre-alarm log and time left
  function getPreAlarm(sensorId: number) {
    if (alarmSettingsMap[sensorId]?.alarms_disabled) return { preAlarm: null, preAlarmTimeLeft: null };
    const preAlarmLog = alarmLog.find(log => log.sensor_id === sensorId && !log.active && !log.acknowledged && !log.alarm_reset);
    if (!preAlarmLog) return { preAlarm: null, preAlarmTimeLeft: null };
    const alarmSetting = alarmSettingsMap[sensorId];
    const delayMin = Number(alarmSetting?.delay) || 0;
    const activated = new Date(preAlarmLog.activated).getTime();
    const timeLeft = activated + delayMin * 60 * 1000 - now;
    // Delay elapsed — stop showing pre-alarm UI regardless of whether the DB entry
    // has been cleaned up yet. The active alarm (or nothing) takes over from here.
    if (timeLeft <= 0) return { preAlarm: null, preAlarmTimeLeft: null };
    return { preAlarm: preAlarmLog, preAlarmTimeLeft: timeLeft };
  }

  useEffect(() => {
    document.documentElement.style.setProperty(
      '--dragging-bg',
      document.documentElement.classList.contains('dark') ? '#1f2937' : '#f9fafb'
    );
  }, []);

  // Removed duplicate liveData query - now passed as prop

  // Apply order from localStorage whenever the sensors list changes (no API call).
  useEffect(() => {
    if (sensors.length === 0) { setOrderedSensors([]); return; }
    const savedOrder = localStorage.getItem('sensorOrder');
    if (savedOrder && enableDragAndDrop) {
      try {
        const orderMap = new Map<string, number>(JSON.parse(savedOrder));
        setOrderedSensors(createOrderedSensorList(sensorsRef.current, orderMap));
      } catch {
        setOrderedSensors([...sensorsRef.current]);
      }
    } else {
      setOrderedSensors([...sensorsRef.current]);
    }
  }, [sensors, enableDragAndDrop]);

  // Fetch persisted order from the API once per user/DnD-toggle — NOT on every sensors poll.
  useEffect(() => {
    if (!enableDragAndDrop || !user?.id) return;
    let effectActive = true;
    const startedAt = Date.now();

    (async () => {
      try {
        const response = await fetch(
          `${API_CONFIG.baseURL}/user-preferences?userId=${user.id}`,
          { method: 'GET', credentials: 'include', headers: prepareCommonHeaders() }
        );
        if (!effectActive || !response.ok) return;
        const data = await response.json();
        if (!effectActive) return;

        const entries: any[] = Array.isArray(data) ? data : (data.sensorOrder ? null : []);
        let orderMap = new Map<string, number>();

        if (entries) {
          entries
            .filter((item: any) => item.preference_key?.startsWith('sensor_order_'))
            .forEach((item: any) => {
              const sensorId = item.preference_key.replace('sensor_order_', '');
              const order = parseInt(item.preference_value);
              if (!isNaN(order)) orderMap.set(sensorId, order);
            });
        } else if (data.sensorOrder && Array.isArray(data.sensorOrder)) {
          data.sensorOrder.forEach((item: any) => orderMap.set(item.sensorId.toString(), item.order));
        }

        if (orderMap.size > 0) {
          localStorage.setItem('sensorOrder', JSON.stringify([...orderMap]));
          if (!effectActive || startedAt < lastDragAt.current) return;
          setOrderedSensors(createOrderedSensorList(sensorsRef.current, orderMap));
        }
      } catch (error) {
        console.error('Failed to load sensor order from database:', error);
      }
    })();

    return () => { effectActive = false; };
  }, [enableDragAndDrop, user?.id, user?.companyID]);

  // Helper function to create ordered sensor list
  const createOrderedSensorList = (sensors: Sensor[], orderMap: Map<string, number>): Sensor[] => {
    // Create an array of sensors with their order
    const sensorsWithOrder = sensors.map(sensor => ({
      sensor,
      order: orderMap.get(sensor.id.toString()) ?? Number.MAX_SAFE_INTEGER // Put unordered sensors at the end
    }));
    
    // Sort by order
    sensorsWithOrder.sort((a, b) => a.order - b.order);
    
    // Return just the sensors in the correct order
    return sensorsWithOrder.map(item => item.sensor);
  };

  // Alarming sensors float to the top; the rest stay in the user's saved drag-drop order.
  // IMPORTANT: return the same orderedSensors reference whenever no sensors are actually
  // alarming — alarmLog polls every few seconds and a new array reference on every poll
  // triggers unnecessary Draggable re-renders mid-drag, causing @hello-pangea/dnd to snap back.
  const displayedSensors = useMemo(() => {
    if (!showAlarmUi || orderedSensors.length === 0) return orderedSensors;
    const alarming: Sensor[] = [];
    const calm: Sensor[] = [];
    for (const s of orderedSensors) {
      const logs = alarmLog.filter(
        (log: any) => log.sensor_id === s.id && log.active && !log.acknowledged && !log.alarm_reset
      );
      const isAlarming = !alarmSettingsMap[s.id]?.alarms_disabled && logs.length > 0;
      if (isAlarming) alarming.push(s);
      else calm.push(s);
    }
    // No active alarms — return the original reference so the Draggable list stays
    // referentially stable and drag-and-drop is not disrupted by polling re-renders.
    if (alarming.length === 0) return orderedSensors;
    // Within alarming group: most recently triggered first
    alarming.sort((a, b) => {
      const aLogs = alarmLog.filter((l: any) => l.sensor_id === a.id && l.active && !l.acknowledged && !l.alarm_reset);
      const bLogs = alarmLog.filter((l: any) => l.sensor_id === b.id && l.active && !l.acknowledged && !l.alarm_reset);
      const aTime = aLogs.length ? Math.max(...aLogs.map((l: any) => new Date(l.activated).getTime())) : 0;
      const bTime = bLogs.length ? Math.max(...bLogs.map((l: any) => new Date(l.activated).getTime())) : 0;
      return bTime - aTime;
    });
    return [...alarming, ...calm];
  }, [orderedSensors, alarmLog, alarmSettingsMap, showAlarmUi]);

  const navigate = useNavigate();

  const handleRowClick = (sensorId: number, channel?: 1 | 2) => {
    const path = channel ? `/sensors/${sensorId}?channel=${channel}` : `/sensors/${sensorId}`;
    navigate(path);
  };

  const handleDragStart = () => {
    setIsDragging(true);
    // Add a subtle visual feedback
    document.body.style.cursor = 'grabbing';
  };

  const handleDragEnd = async (result: any) => {
    lastDragAt.current = Date.now();
    if (!result.destination) {
      setIsDragging(false);
      return;
    }

    const items = Array.from(displayedSensors); // same ref as orderedSensors when no alarms
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    setOrderedSensors(items);
    setIsDragging(false);
    document.body.style.cursor = '';

    // Save order to localStorage for immediate persistence
    const orderMap = new Map((items as Sensor[]).map((item, index) => [item.id.toString(), index]));
    localStorage.setItem('sensorOrder', JSON.stringify([...orderMap]));
    
    // Persist sensor order to database via user-preferences API
    try {
      await Promise.all(
        Array.from(orderMap.entries()).map(([sensorId, order]) =>
          fetch(`${API_CONFIG.baseURL}/user-preferences`, {
            method: 'POST',
            credentials: 'include',
            headers: prepareCommonHeaders(),
            body: JSON.stringify({
              userId: user?.id,
              preference_key: `sensor_order_${sensorId}`,
              preference_value: order.toString(),
            }),
          })
        )
      );

      // Show success feedback
      const successMessage = document.createElement('div');
      successMessage.className = 'fixed top-4 right-4 bg-green-500 text-white px-4 py-2 rounded shadow-lg z-50';
      successMessage.textContent = 'Sensor order saved';
      document.body.appendChild(successMessage);
      
      setTimeout(() => {
        document.body.removeChild(successMessage);
      }, 2000);
    } catch (error) {
      console.error('Failed to save sensor order to database:', error);
      
      // Show error feedback
      const errorMessage = document.createElement('div');
      errorMessage.className = 'fixed top-4 right-4 bg-red-500 text-white px-4 py-2 rounded shadow-lg z-50';
      errorMessage.textContent = 'Failed to save sensor order';
      document.body.appendChild(errorMessage);
      
      setTimeout(() => {
        document.body.removeChild(errorMessage);
      }, 3000);
    }
    
    // Add a brief success feedback
    const draggedElement = document.querySelector('.drag-success-feedback');
    if (draggedElement) {
      draggedElement.classList.add('animate-pulse');
      setTimeout(() => {
        draggedElement.classList.remove('animate-pulse');
      }, 500);
    }
  };

  // Find the latest alarm log entry for a sensor in alarm state
  function getLatestAlarm(sensorId: number) {
    if (alarmSettingsMap[sensorId]?.alarms_disabled) return null;
    const logs = alarmLog.filter(log => log.sensor_id === sensorId && log.active && !log.acknowledged && !log.alarm_reset);
    if (logs.length === 0) return null;
    // Return the most recent
    return logs.reduce((a, b) => new Date(a.activated) > new Date(b.activated) ? a : b);
  }

  const handleAlarmModalAcknowledge = async () => {
    if (!alarmModal?.anchorLog?.event_id) return;
    const trimmedNote = alarmNoteDraft.trim();
    if (!trimmedNote) {
      setAlarmAckError('Please enter a note before acknowledging.');
      return;
    }
    const sensorId = alarmModal.sensor.id;
    const eventId = alarmModal.anchorLog.event_id;
    const sensorName = alarmModal.sensor.name;
    const alarmType = alarmModal.anchorLog.alarm_type;

    const logToAck = alarmLog.find(
      (log: any) =>
        log.sensor_id === sensorId &&
        log.event_id === eventId &&
        log.active &&
        !log.acknowledged &&
        !log.alarm_reset
    );
    if (!logToAck) {
      setAlarmAckError('This alarm may have already been acknowledged. Refresh and try again.');
      return;
    }

    const logsToAcknowledge = alarmLog.filter(
      (log: any) => log.event_id === eventId && !log.acknowledged
    );

    const noteWithUser = `${trimmedNote} —${userDisplayName}`;

    setAlarmAckSubmitting(true);
    setAlarmAckError(null);
    try {
      await Promise.all(
        logsToAcknowledge.map(l =>
          acknowledgeAlarmLog({
            logId: l.idAlarmLog,
            body: {
              acknowledged: true,
              acknowledged_by: userDisplayName,
              alarm_reset: new Date().toISOString(),
              notes: noteWithUser,
            },
          })
        )
      );

      await logAuditEvent(
        AUDIT_ACTIONS.ALARM_ACKNOWLEDGE,
        `Alarm acknowledged from Dashboard - Event ID: ${eventId}, Sensor ID: ${sensorId}, Sensor Name: ${sensorName}, Alarm Type: ${alarmType}, Acknowledged by: ${userDisplayName}`,
        user
      );

      dispatch(alarmApi.util.invalidateTags(['Alarms', 'AlarmLog']));
      setAlarmModal(null);
      setAlarmNoteDraft('');
    } catch (error) {
      console.error('Failed to acknowledge alarm:', error);
      setAlarmAckError('Could not acknowledge alarm. Please try again.');
    } finally {
      setAlarmAckSubmitting(false);
    }
  };

  // Add a no-op function for when drag-and-drop is disabled
  const noop = () => {};

  // Calibration modal state
  const [calibrateSensor, setCalibrateSensor] = useState<Sensor | null>(null);
  const [setpoint, setSetpoint] = useState('');
  const [offset, setOffset] = useState('');
  const [calLoading, setCalLoading] = useState(false);
  const [calError, setCalError] = useState<string | null>(null);
  const [calSuccess, setCalSuccess] = useState(false);
  const [clearLoading, setClearLoading] = useState(false);
  const [clearError, setClearError] = useState<string | null>(null);
  const [clearSuccess, setClearSuccess] = useState(false);

  async function sendCalibration(sensorId: number, setpoint: string, offset: string) {
    const response = await fetch(`${API_CONFIG.baseURL}/calibrate`, {
      method: 'POST',
      credentials: 'include',
      headers: prepareCommonHeaders(),
      body: JSON.stringify({ sensorId, setpoint, offset }),
    });
    if (!response.ok) {
      throw new Error('Failed to send calibration');
    }
    return await response.json();
  }

  const handleCalibrateSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!calibrateSensor) return;
    setCalLoading(true);
    setCalError(null);
    setCalSuccess(false);
    try {
      await sendCalibration(calibrateSensor.id, setpoint, offset);
      
      // Log successful calibration
      await logAuditEvent(
        AUDIT_ACTIONS.SENSOR_CALIBRATE,
        `Sensor calibrated - Sensor ID: ${calibrateSensor.id}, Name: ${calibrateSensor.name}, Setpoint: ${setpoint}, Offset: ${offset}`,
        user
      );
      
      setCalSuccess(true);
      setTimeout(() => {
        setCalibrateSensor(null);
        setSetpoint('');
        setOffset('');
        setCalSuccess(false);
      }, 1200);
    } catch (err: any) {
      setCalError(err.message || 'Failed to calibrate');
    } finally {
      setCalLoading(false);
    }
  };

  const handleCalibrateClose = () => {
    setCalibrateSensor(null);
    setSetpoint('');
    setOffset('');
    setCalError(null);
    setCalSuccess(false);
  };

  async function clearCalibration(sensorId: number) {
    const response = await fetch(`${API_CONFIG.baseURL}/calibrate`, {
      method: 'POST',
      credentials: 'include',
      headers: prepareCommonHeaders(),
      body: JSON.stringify({ sensorId, action: 'CLEAR' }),
    });
    if (!response.ok) {
      throw new Error('Failed to clear calibration');
    }
    return await response.json();
  }

  const handleClearCalibration = async () => {
    if (!calibrateSensor) return;
    setClearLoading(true);
    setClearError(null);
    setClearSuccess(false);
    try {
      await clearCalibration(calibrateSensor.id);
      
      // Log successful calibration clear
      await logAuditEvent(
        AUDIT_ACTIONS.SENSOR_CALIBRATE_CLEAR,
        `Sensor calibration cleared - Sensor ID: ${calibrateSensor.id}, Name: ${calibrateSensor.name}`,
        user
      );
      
      setClearSuccess(true);
      setTimeout(() => {
        setClearSuccess(false);
        setCalibrateSensor(null);
      }, 1200);
    } catch (err: any) {
      setClearError(err.message || 'Failed to clear calibration');
    } finally {
      setClearLoading(false);
    }
  };

  return (
    <DragDropContext onDragStart={enableDragAndDrop ? handleDragStart : noop} onDragEnd={enableDragAndDrop ? handleDragEnd : noop}>
      <div className="w-full max-w-full min-w-0 overflow-x-auto overscroll-x-contain">
        {enableDragAndDrop && sensors.length > 0 && (
          <p className="mb-3 px-1 sm:px-0 text-sm text-gray-600 dark:text-gray-400 leading-snug flex flex-wrap items-center gap-x-2 gap-y-1">
            <span>
              Click any row to open its chart. Use the
              {' '}
              <GripHorizontal className="inline-block h-3.5 w-3.5 align-text-bottom mx-px text-gray-500" aria-hidden />
              {' '}
              handle to reorder.
            </span>
          </p>
        )}
        <table
          className={`w-full max-w-full min-w-0 table-fixed sm:table-auto ${rowBorders ? 'border-separate border-spacing-y-2' : 'divide-y divide-gray-200 dark:divide-gray-700'}`}
        >
          <thead className={`bg-gray-50 dark:bg-gray-800 ${rowBorders ? 'border-b border-gray-200 dark:border-gray-700' : ''}`}>
            <tr>
              <th scope="col" className={`min-w-0 px-1 sm:px-6 py-1.5 sm:py-2 text-left text-[10px] sm:text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider ${hideDataColumn ? 'w-[40%] sm:w-auto' : 'w-[32%] sm:w-auto'}`}>
                Sensor Name
              </th>
              {!hideDataColumn && (
                <th scope="col" className="w-[28%] sm:w-auto min-w-0 px-1 sm:px-4 py-1.5 sm:py-2 text-left text-[10px] sm:text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Data
                </th>
              )}
              <th scope="col" className={`min-w-0 px-1 sm:px-4 py-1.5 sm:py-2 text-left text-[10px] sm:text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider ${hideDataColumn ? 'w-[32%] sm:w-auto' : 'w-[22%] sm:w-auto'}`}>
                Status
              </th>
              <th scope="col" className={`px-1 sm:px-4 py-1.5 sm:py-2 text-left text-[10px] sm:text-xs font-medium uppercase tracking-wider ${signalTestIds.size > 0 ? 'table-cell text-orange-500 dark:text-orange-400' : 'hidden md:table-cell text-gray-500 dark:text-gray-400'}`}>
                {signalTestIds.size > 0 ? '⚡ Last Seen' : 'Last Seen'}
              </th>
              {showActions && (
                <th
                  scope="col"
                  className={`min-w-0 px-1 sm:px-4 py-1.5 sm:py-2 text-[10px] sm:text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider ${
                    preferActionMenu ? 'w-14 text-center text-gray-400 dark:text-gray-500 sm:w-[5.25rem]' : 'w-[18%] sm:w-auto text-right'
                  }`}
                  title={preferActionMenu ? 'Sensor actions per row' : undefined}
                  aria-label={preferActionMenu ? 'Actions' : undefined}
                >
                  {preferActionMenu ? <span aria-hidden className="text-base font-bold normal-case tracking-normal opacity-70">⋯</span> : 'Actions'}
                </th>
              )}
            </tr>
          </thead>
          <Droppable droppableId="sensors" isDropDisabled={!enableDragAndDrop}>
            {(droppableProvided) => (
              <tbody
                {...droppableProvided.droppableProps}
                ref={droppableProvided.innerRef}
                className={
                  rowBorders
                    ? 'bg-transparent dark:bg-transparent'
                    : 'bg-app-panel divide-y divide-gray-200 dark:bg-gray-900 dark:divide-gray-700'
                }
              >
                {(displayedSensors as SensorRow[])
                  .filter((sensor: SensorRow) => sensor && sensor.id !== undefined && sensor.id !== null)
                  .map((sensor: SensorRow, index: number) => {
                    const latestAlarm = showAlarmUi ? getLatestAlarm(sensor.id) : null;
                    const { preAlarm, preAlarmTimeLeft } = showAlarmUi
                      ? getPreAlarm(sensor.id)
                      : { preAlarm: null, preAlarmTimeLeft: null };
                    const draggableKey = isSplitRow(sensor)
                      ? `${sensor.id}-ch${sensor._channel}`
                      : sensor.id.toString();
                    return (
                      <Draggable
                        key={draggableKey}
                        draggableId={draggableKey}
                        index={index}
                        isDragDisabled={!enableDragAndDrop || isSplitRow(sensor)}
                      >
                        {(draggableProvided, snapshot) => (
                          <SortableSensorRow
                            sensor={sensor}
                            liveData={liveData}
                            onRowClick={handleRowClick}
                            provided={draggableProvided}
                            isDragging={snapshot.isDragging}
                            latestAlarm={latestAlarm}
                            preAlarm={preAlarm}
                            preAlarmTimeLeft={preAlarmTimeLeft ?? 0}
                            backfillStatus={backfillStatus}
                            onUpdateFirmware={onUpdateFirmware}
                            showFirmwareUpdate={showFirmwareUpdate}
                            latestFirmwareVersion={latestFirmwareVersion}
                            onEdit={onEdit}
                            onDelete={onDelete}
                            onToggleServiceStatus={onToggleServiceStatus}
                            enableDragAndDrop={enableDragAndDrop}
                            onCalibrate={setCalibrateSensor}
                            showActions={showActions}
                            onAlarmBellClick={showAlarmUi ? openAlarmModal : undefined}
                            now={now}
                            showAlarmUi={showAlarmUi}
                            rowBorders={rowBorders}
                            showLiveTemp={showLiveTemp}
                            preferActionMenu={preferActionMenu}
                            adminListSurface={adminListSurface}
                            hideDataColumn={hideDataColumn}
                            isSignalTest={signalTestIds.has(Number(sensor.id))}
                          />
                        )}
                      </Draggable>
                    );
                  })}
                {droppableProvided.placeholder}
              </tbody>
            )}
          </Droppable>
        </table>
      </div>
      <AlarmAcknowledgeDialog
        isOpen={!!alarmModal}
        onClose={closeAlarmModal}
        alarmType={String(alarmModal?.anchorLog?.alarm_type ?? '')}
        sensorName={alarmModal?.sensor.name ?? ''}
        serialDisplay={
          alarmModal ? formatSensorSerial(alarmModal.sensor.serialNo, alarmModal.sensor.type) : ''
        }
        alarmValues={alarmModal?.anchorLog?.alarm_values}
        activatedAt={alarmModal?.anchorLog?.activated}
        alarmResetAt={alarmModal?.anchorLog?.alarm_reset ?? null}
        eventId={alarmModal?.anchorLog?.event_id}
        noteDraft={alarmNoteDraft}
        onNoteChange={setAlarmNoteDraft}
        onAcknowledge={handleAlarmModalAcknowledge}
        isSubmitting={alarmAckSubmitting}
        error={alarmAckError}
      />
      {/* Calibration modal */}
      <Dialog isOpen={!!calibrateSensor} onClose={handleCalibrateClose}>
        <form onSubmit={handleCalibrateSubmit} className="space-y-4">
          <div className="text-lg font-semibold mb-2">Calibrate Sensor</div>
          <div className="text-sm text-gray-500 mb-2">
            Sensor: <span className="font-medium text-gray-900 dark:text-white">{calibrateSensor?.name}</span>
          </div>
          <div>
            <label className="block text-xs font-medium mb-1">Setpoint</label>
            <input
              type="number"
              step="any"
              className="w-full border rounded px-2 py-1 text-sm"
              value={setpoint}
              onChange={e => setSetpoint(e.target.value)}
              required
              disabled={calLoading || clearLoading}
            />
          </div>
          <div>
            <label className="block text-xs font-medium mb-1">Offset</label>
            <input
              type="number"
              step="any"
              className="w-full border rounded px-2 py-1 text-sm"
              value={offset}
              onChange={e => setOffset(e.target.value)}
              required
              disabled={calLoading || clearLoading}
            />
          </div>
          {calError && <div className="text-red-600 text-xs">{calError}</div>}
          {calSuccess && <div className="text-green-600 text-xs">Calibration sent successfully!</div>}
          {clearError && <div className="text-red-600 text-xs">{clearError}</div>}
          {clearSuccess && <div className="text-green-600 text-xs">Calibration cleared successfully!</div>}
          <div className="flex justify-between space-x-2 mt-4">
            <div>
              <button
                type="button"
                className="px-3 py-1 rounded bg-red-600 text-white text-xs font-semibold hover:bg-red-700 disabled:opacity-60"
                onClick={handleClearCalibration}
                disabled={clearLoading || calLoading}
              >
                {clearLoading ? 'Clearing...' : 'Clear Calibration'}
              </button>
            </div>
            <div className="flex space-x-2">
              <button
                type="button"
                className="px-3 py-1 rounded bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 text-xs"
                onClick={handleCalibrateClose}
                disabled={calLoading || clearLoading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-3 py-1 rounded bg-yellow-600 text-white text-xs font-semibold hover:bg-yellow-700 disabled:opacity-60"
                disabled={calLoading || clearLoading}
              >
                {calLoading ? 'Sending...' : 'Submit'}
              </button>
            </div>
          </div>
        </form>
      </Dialog>
    </DragDropContext>
  );
}